import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { ProgressIndicator } from 'app/design/primitives/progress-indicator';
import { Text } from 'app/design/primitives/text';
import { images } from 'app/design/images';

import { useScopedTranslation } from 'app/hooks/i18n';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { ChooseSharerTypeOptions } from 'app/screens/onboarding/choose-sharer-type.shared';
import { useChooseSharerTypeFlow } from 'app/screens/onboarding/hooks/use-choose-sharer-type-flow';
import {
  ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
  onboardingLargeModalHeight,
} from 'app/screens/onboarding/onboarding-large-modal-layout';
import { useOnboardingNavigation } from 'app/screens/onboarding/credentials';
import type { NavigationScreen } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

const ChooseSharerTypeScreenComponent: NavigationScreen<'choose-sharer-type'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const flow = useChooseSharerTypeFlow(navigation);
  const { current, max } = useOnboarding();
  const { t: tOnboarding } = useTranslation('onboarding');

  const setPageTitle = usePageTitle('choose-sharer-type');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const { progressActiveColor } = useOnboardingNavigation(flow.userInfo?.type, { headerPresentation: 'overlay' });
  const { height: windowHeight } = useWindowDimensions();
  const modalHeight = onboardingLargeModalHeight(windowHeight);
  const insets = useSafeAreaInsets();

  const primaryProps = {
    disabled: !flow.isSharerTypeSelected || flow.isPending,
    loading: flow.isPending,
    onPress: () => {
      void flow.saveSharerTypePreferences();
    },
  };

  const showProgress = !Boolean(flow.userInfo?.acceptedGuidelineDate);

  return (
    <View style={styles.root}>
      <PCImage
        cachePolicy="memory-disk"
        contentFit="cover"
        source={images.web.quick_choose}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.scrim} />
      <SafeAreaView edges={['top', 'left', 'right']} style={styles.safe}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.keyboard}>
          <View style={styles.modalWrap}>
            <Column
              alignSelf="center"
              backgroundColor="surface.default"
              borderRadius="radius.xl"
              gap="spacing.general.x4"
              maxWidth={480}
              paddingHorizontal="spacing.general.x4"
              paddingTop="spacing.general.x4"
              style={[styles.card, { height: modalHeight }]}
              width="100%"
            >
              <ScrollView
                contentContainerStyle={styles.scrollInner}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
                style={styles.scroll}
              >
                {showProgress ? (
                  <ProgressIndicator activeColor={progressActiveColor} index={current} length={max} />
                ) : null}

                <Text
                  fontWeight="600"
                  marginBottom="spacing.general.x4"
                  marginTop="spacing.general.x8"
                  textAlign="center"
                  variant="headerXSmall"
                >
                  {t('title')}
                </Text>

                <ChooseSharerTypeOptions
                  disabled={flow.isPending}
                  gap="spacing.general.x5"
                  selectedSharerType={flow.selectedSharerType}
                  onSelect={flow.onSelect}
                />
              </ScrollView>

              <Column style={{ paddingBottom: Math.max(insets.bottom, theme.spacing['spacing.general.x4']) }}>
                <Button variant="primary" {...primaryProps}>
                  <Text color="surface.default" textAlign="center">
                    {current === max - 1 ? tOnboarding('confirm') : tOnboarding('nextStep')}
                  </Text>
                </Button>
              </Column>
            </Column>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
};

export const ChooseSharerTypeScreen: NavigationScreen<'choose-sharer-type'> = withErrorBoundary(
  ChooseSharerTypeScreenComponent,
);

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: theme.colors['text.primary'],
  },
  scrim: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0,0,0,0.42)',
  },
  safe: {
    flex: 1,
  },
  keyboard: {
    flex: 1,
  },
  modalWrap: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: ONBOARDING_LARGE_OVERLAY_HEADER_HEIGHT,
  },
  scroll: {
    flex: 1,
  },
  scrollInner: {
    paddingBottom: theme.spacing['spacing.general.x2'],
  },
  card: {
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
  },
});
